import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Column, Collapse, Select, TextInput, Button, Alert, Icon } from '@pella/sash-framework';
import { Video } from '../../../';
import { getVisibleGlassMeasurements } from './visible-glass-util';
import { setSessionStorage } from '../../../utilities/functions';

import styles from './index.module.scss';

const CalculateMeasurements = ({ backendName, item, productType, content, onClose, getCalculations, sendAnswer, setLoading, originalMeasurements, setMeasureStatus, errorData }) => {
	const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
	const [values, setValues] = useState({});
	const [errors, setErrors] = useState({});
	const [status, setStatus] = useState('idle');
	const [showConversionTable, setShowConversionTable] = useState(null);

	useEffect(() => {
		let newValues = {};

		if (content?.visible_glass?.box?.find((box) => box.box_id === 'productLine')?.product_values?.find((line) => backendName.includes(line.be_name))) {
			const productLine = content.visible_glass.box.find((box) => box.box_id === 'productLine').product_values.find((line) => backendName.includes(line.be_name));
			newValues.productLine = productLine.be_name;
			newValues.product_types = productLine.product_type;

			// use some to find entries who have a west and east backend name
			if (productLine?.product_type?.find((type) => type.be_name.split(', ')?.some((name) => backendName.includes(name)))) {
				const productType = productLine.product_type.find((type) => type.be_name.split(', ')?.some((name) => backendName.includes(name)));
				newValues.windowType = productType.be_name;
			}

			if (productLine?.be_name?.toLowerCase().includes('lifestyle')) {
				newValues.hingedGlassPanel = item?.current_step_data?.questions?.find((question) => question.question_text === 'Hinged Glass Panel Option')?.selected_answer_id;
			}
		}

		if (originalMeasurements?.width && originalMeasurements?.height) {
			newValues.original = originalMeasurements;
			newValues.visibleGlassWidth = originalMeasurements.width;
			newValues.visibleGlassHeight = originalMeasurements.height;
		}

		if (Object.keys(newValues).length > 0) setValues((curr) => ({ ...curr, ...newValues }));
	}, [content, backendName, originalMeasurements, item?.current_step_data]);

	const onInputChange = (e) => {
		if (status === 'show-results') setStatus('idle');

		if (e.target.options?.[e.target.selectedIndex]?.dataset?.types) {
			setValues({
				...values,
				[e.target.name]: e.target.value,
				windowType: '',
				product_types: JSON.parse(e.target.options[e.target.selectedIndex].dataset.types),
			});
		} else {
			setValues({ ...values, [e.target.name]: e.target.value });
		}
	};

	const validateForm = () => {
		let newErrors = {};
		let topLevelErrors = [];

		if (!values.productLine) {
			newErrors.productLine = errorData.find((err) => err.field_id === 'productLine')?.error_2;
			topLevelErrors.push(errorData.find((err) => err.field_id === 'productLine')?.error);
		}
		if (!values.windowType) {
			newErrors.windowType = errorData.find((err) => err.field_id === 'windowType')?.error_2;
			topLevelErrors.push(errorData.find((err) => err.field_id === 'windowType')?.error);
		}
		if (!values.visibleGlassWidth || (values.visibleGlassWidth && values.visibleGlassWidth.length < 2)) {
			newErrors.visibleGlassWidth = errorData.find((err) => err.field_id === 'visibleGlassWidth')?.error_2;
			topLevelErrors.push(errorData.find((err) => err.field_id === 'visibleGlassWidth')?.error);
		}
		if (!values.visibleGlassHeight || (values.visibleGlassHeight && values.visibleGlassHeight.length < 2)) {
			newErrors.visibleGlassHeight = errorData.find((err) => err.field_id === 'visibleGlassHeight')?.error_2;
			topLevelErrors.push(errorData.find((err) => err.field_id === 'visibleGlassHeight')?.error);
		}
		if (values?.productLine?.toLowerCase().includes('lifestyle') && !values.hingedGlassPanel) {
			newErrors.hingedGlassPanel = errorData.find((err) => err.field_id === 'hingedGlassPanel')?.error_2;
			topLevelErrors.push(errorData.find((err) => err.field_id === 'hingedGlassPanel')?.error);
		}

		if (Object.keys(newErrors).length > 0) {
			setStatus('error');
			setErrors({ ...newErrors, topLevel: topLevelErrors });
			return false;
		}

		return true;
	};

	const onFormSubmit = (e) => {
		e.preventDefault();
		setErrors({});
		setStatus('pending');

		if (!validateForm()) return false;

		// Breakout the few types that have a west option
		let window_type = values.windowType;
		if (values.windowType.includes(', ') && values.windowType.split(', ').length > 1) {
			const index = backendName.includes('-w-') ? 1 : 0;
			window_type = values.windowType.split(', ')[index];
		}

		const results = getVisibleGlassMeasurements(window_type, values.visibleGlassWidth, values.visibleGlassHeight, values.hingedGlassPanel === 'cbbb1966-d216-4a65-af32-cddd6428903b');
		if (results?.data?.width && results?.data?.height) {
			setValues({
				...values,
				original: { width: values.visibleGlassWidth, height: values.visibleGlassHeight },
				actual: { width: results.data.width.toString(), height: results.data.height.toString() },
			});
			setTimeout(() => setStatus('show-results'), 500);
		} else {
			setStatus('error');
			setErrors({ topLevel: results?.error });
		}
	};

	const onApplyBtnClick = () => {
		setSessionStorage('calculatedMeasurements', { original: { width: values.visibleGlassWidth, height: values.visibleGlassHeight }, actual: { width: values.actual.width, height: values.actual.height } });
		const widthQ = item.current_step_data?.questions?.find((question) => question.question_text.match(/(frame|door\ call|call)\ width/i));
		const heightQ = item.current_step_data?.questions?.find((question) => question.question_text.match(/(frame|door\ call|call)\ height/i));
		const hgpQ = item.current_step_data?.questions?.find((question) => question.question_text === 'Hinged Glass Panel Option');

		sendAnswer(widthQ, values.actual.width);
		sendAnswer(heightQ, values.actual.height);
		if (hgpQ?.id && values.productLine.includes('lifestyle') && hgpQ?.selected_answer_id !== values.hingedGlassPanel) {
			// update HGP answer if answer is yes (default is no)
			sendAnswer(hgpQ, values.hingedGlassPanel);
		}

		setLoading(true);
		getCalculations(values.actual, values.original);

		// update URL if backendName does not include windowType or productType
		// windowType can be 2 values (encompass -w) => split and check if either are included
		if (!values?.windowType?.split(', ').some((type) => backendName.includes(type)) || !backendName.includes(productType)) {
			let url = new URL(window.location.href);
			const urlParams = url.searchParams;
			const brand = content.visible_glass?.box?.find((box) => box.box_id === 'productLine')?.product_values?.find((line) => line.be_name === values.productLine);
			const heading = brand?.product_type?.find((type) => type.be_name === values.windowType)?.fe_name + ' ' + urlParams.get('heading')?.split(' ')?.slice(1)?.join(' ');

			urlParams.set('brand', brand.fe_name);
			urlParams.set('heading', heading);
			urlParams.set('id', values.windowType + `-${productType}`);

			history.pushState(null, '', url.toString());
			history.go();
		} else {
			setMeasureStatus('calculated');
			setTimeout(() => setLoading(false), 1000);
		}
	};

	const onConversionBtnClick = (id) => {
		if (showConversionTable === id) setShowConversionTable(null);
		else setShowConversionTable(id);
	};

	const chunkArray = (arr) => {
		let index = 0;
		let length = arr.length;
		let size = Math.round(arr.length / 2);
		let newArr = [];

		for (index = 0; index < length; index += size) {
			let chunk = arr.slice(index, index + size);
			newArr.push(chunk);
		}

		return newArr;
	};

	const conversion_groups = content?.visible_glass?.conversions?.conversion_data ? chunkArray(content.visible_glass.conversions.conversion_data) : [];

	const renderConversionTable = () => (
		<div className='mb-4 bg-neutral-50 p-4 text-center md:mb-0'>
			<Row>
				{conversion_groups.map((group, index) => (
					<Column
						xs={12}
						md={6}
						key={index}
						className={index > 0 ? styles.conversionColLast : null}
					>
						<Row justify='center'>
							<Column
								xs={5}
								className={`text-sm font-semibold ${index > 0 ? 'hidden md:block' : ''}`}
							>
								{content.visible_glass.conversions.header_row?.split(',')?.[0]}
							</Column>
							<Column
								xs={5}
								className={`text-sm font-semibold ${index > 0 ? 'hidden md:block' : ''}`}
							>
								{content.visible_glass.conversions.header_row?.split(',')?.[1]}
							</Column>

							{group.map((col, index) => (
								<React.Fragment key={index}>
									<Column
										xs={5}
										className='py-1.5 text-sm'
									>
										{col?.split(',')?.[0]}
									</Column>
									<Column
										xs={5}
										className='py-1.5 text-sm'
									>
										{col?.split(',')?.[1]}
									</Column>
								</React.Fragment>
							))}
						</Row>
					</Column>
				))}
			</Row>
		</div>
	);

	return (
		<div className={`${styles.calculator} h-full w-full bg-white py-4 md:pb-0 md:pt-8 xl:pb-8`}>
			<Container className='relative'>
				{typeof onClose === 'function' && (
					<button
						className={`mb-5 flex items-center font-semibold`}
						onClick={onClose}
					>
						<Icon
							name='keyboard_arrow_left'
							className='mr-1'
						/>
						Back to Builder
					</button>
				)}

				{content?.heading && <h2 className='text-22px md:text-26px mb-4 font-semibold xl:text-3xl'>{content.heading}</h2>}
				{content?.subheading && <p>{content.subheading}</p>}

				<Row
					className='xl:mt-10'
					verticalGutters
				>
					<Column
						xs={12}
						xl={6}
					>
						{content?.video?.[0] && (
							<Collapse
								className={styles.calculatorCollapse}
								isOpen
							>
								<Collapse.Trigger className='!px-4'>{content?.video_heading}</Collapse.Trigger>
								<Collapse.Content className='!py-0'>
									<Video
										data={content.video[0]}
										className={styles.calculatorVideo}
									/>
								</Collapse.Content>
							</Collapse>
						)}
					</Column>

					<Column
						xs={12}
						xl={6}
					>
						{status === 'error' && errors?.topLevel?.[0] && (
							<Alert
								type='error'
								className='mb-4'
								hideClose
							>
								{errors.topLevel.map((err, index) => (
									<div
										key={index}
										dangerouslySetInnerHTML={{ __html: err }}
									/>
								))}
							</Alert>
						)}

						{content?.visible_glass?.heading && <h2 className='xl:text-26px mb-2 text-sm font-semibold md:text-lg'>{content.visible_glass.heading}</h2>}
						{content?.visible_glass?.subheading && <p className='mb-8'>{content.visible_glass.subheading}</p>}

						<form
							onSubmit={onFormSubmit}
							noValidate
						>
							<Row className={styles.calculatorFormRow}>
								{content?.visible_glass?.box?.[0] &&
									content.visible_glass.box.map((box, index) => {
										let customContent;
										if (box.box_id === 'hingedGlassPanel' && !values?.productLine?.toLowerCase().includes('lifestyle')) return null;
										if (box.box_id === 'hingedGlassPanel' && !values?.windowType.includes('lifestyle-casement') && !values.windowType.includes('lifestyle-awning')) return null;

										if (box.customization?.[0] && box.customization.find((item) => item.backend_name.includes(backendName))) {
											customContent = box.customization.find((item) => item.backend_name.includes(backendName));
										}

										return (
											<React.Fragment key={index}>
												<Column
													xs={12}
													md={6}
												>
													{/* <p className='text-required mb-1 text-sm font-semibold'>{box.box_heading}</p> */}
													{box.input_type === 'select' && (
														<Select
															id={box.box_id}
															labelText={box.box_heading}
															onChange={onInputChange}
															value={values[box.box_id] || ''}
															error={errors[box.box_id] ? true : false}
															errorText={errors[box.box_id]}
															required
														>
															<option value=''>Select an option</option>
															{box.product_values?.[0] &&
																box.product_values.map((opt, index) => (
																	<option
																		key={index}
																		value={opt.be_name}
																		data-types={opt.product_type?.[0] ? JSON.stringify(opt.product_type) : null}
																	>
																		{opt.fe_name}
																	</option>
																))}
															{!box.product_values?.[0] &&
																values?.product_types?.[0] &&
																values.product_types.map((opt, index) => (
																	<option
																		key={index}
																		value={opt.be_name}
																	>
																		{opt.fe_name}
																	</option>
																))}
														</Select>
													)}

													{box.input_type === 'manual' && (
														<TextInput
															type='number'
															id={box.box_id}
															labelText={box.box_heading}
															assistiveText={box.increments}
															onChange={onInputChange}
															value={values[box.box_id] || ''}
															error={errors[box.box_id] ? true : false}
															errorText={errors[box.box_id]}
															step={0.0625}
															groupClass={`${styles.calculatorInputWrapper} mb-2`}
															iconRight={<span className='text-sm font-semibold'>inches</span>}
															required
														/>
													)}

													{box.trigger_text && (
														<button
															type='button'
															onClick={() => onConversionBtnClick(box.box_id)}
															className='-mt-3 ml-auto block text-xs font-semibold uppercase'
														>
															{showConversionTable === box.box_id ? box.trigger_text?.split(', ')[1] : box.trigger_text?.split(', ')[0]}
														</button>
													)}

													{customContent?.instruction && <p className='mb-4 text-sm md:mb-0'>{customContent.instruction}</p>}
													{isMobile && box.input_type === 'manual' && showConversionTable === box.box_id && renderConversionTable()}
												</Column>

												{box.box_id === 'hingedGlassPanel' && (
													<Column
														xs={12}
														md={6}
													/>
												)}
											</React.Fragment>
										);
									})}

								{!isMobile && showConversionTable && <Column xs={12}>{renderConversionTable()}</Column>}

								{status !== 'show-results' && (
									<Column
										xs={12}
										className='mt-8 text-center xl:text-right'
									>
										<Button
											color='primary-alt'
											type='submit'
											size='sm'
											disabled={status === 'pending'}
											className='inline-flex items-center justify-center pb-3'
										>
											{status === 'pending' && (
												<Icon
													name='circle'
													className={`${styles.calculatorLoader} mb-1 mr-2`}
												/>
											)}
											{content?.calculate_actual_size}
										</Button>
									</Column>
								)}
							</Row>
						</form>

						{status === 'show-results' && (
							<div className={`${styles.calculatorResults} mt-8 bg-neutral-50 p-4 text-center`}>
								{content?.results?.heading && (
									<h3 className='mb-3 flex items-center justify-center text-base font-semibold uppercase md:text-xl'>
										{content.results.icon && (
											<Icon
												name={content.results.icon}
												className='mr-2 flex items-center justify-center rounded-full bg-white'
											/>
										)}
										{content.results.heading}
									</h3>
								)}

								{content?.results?.subheading && <p>{content.results.subheading}</p>}
								<p className='mt-4 flex flex-row items-center justify-center'>
									<span className={`${styles.calculatorResultsUnit} text-right font-bold`}>{content?.results?.width_height?.split(', ')?.[0]}:</span>
									<span className={`ml-2 text-left`}>
										{values?.actual?.width} {content?.results?.units}
									</span>
								</p>
								<p className='mt-1.5 flex flex-row items-center justify-center'>
									<span className={`${styles.calculatorResultsUnit} text-right font-bold`}>{content?.results?.width_height?.split(', ')?.[1]}:</span>
									<span className={`ml-2 text-left`}>
										{values?.actual?.height} {content?.results?.units}
									</span>
								</p>

								<Button
									color='primary-alt'
									onClick={onApplyBtnClick}
									className='mt-5'
								>
									{content?.results?.cta}
								</Button>
							</div>
						)}

						{content?.visible_glass?.need_help && (
							<div
								className={`text-center ${status !== 'show-results' ? 'xl:text-right' : ''} mt-4 font-semibold uppercase tracking-wide`}
								dangerouslySetInnerHTML={{ __html: content.visible_glass.need_help }}
							/>
						)}
					</Column>
				</Row>
			</Container>
		</div>
	);
};

CalculateMeasurements.propTypes = {
	backendName: PropTypes.string,
	item: PropTypes.object,
	productType: PropTypes.string,
	content: PropTypes.object,
	onClose: PropTypes.func,
	getCalculations: PropTypes.func,
	originalMeasurements: PropTypes.object,
	setMeasureStatus: PropTypes.func,
	sendAnswer: PropTypes.func,
	setLoading: PropTypes.func,
	errorData: PropTypes.array,
};

export default CalculateMeasurements;
